// frontend/src/components/subscription/SubscriptionSettings.tsx
import React, { useState, useEffect } from 'react';
import { ArrowRight, Check, Award } from 'react-feather';
import SubscriptionService from '../../services/subscription.service';

interface SubscriptionStatus {
  isPremium: boolean;
  planType: 'basic' | 'premium' | 'ultimate';
  expiresAt?: string;
  daysRemaining?: number;
}

const SubscriptionSettings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [processingAction, setProcessingAction] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const [selectedPlan, setSelectedPlan] = useState<'premium' | 'ultimate'>('premium');
  const [subscription, setSubscription] = useState<SubscriptionStatus>({
    isPremium: false,
    planType: 'basic'
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Conversion rate: 1 USD = 1500 NGN (assumed)
  const USD_TO_NGN = 1500;

  // Pricing for plans
  const planPricing = {
    premium: {
      monthly: { usd: 9.99, ngn: 9.99 * USD_TO_NGN },
      yearly: { usd: 7.99 * 12, ngn: 7.99 * 12 * USD_TO_NGN }
    },
    ultimate: {
      monthly: { usd: 20, ngn: 20 * USD_TO_NGN },
      yearly: { usd: 16 * 12, ngn: 16 * 12 * USD_TO_NGN }
    }
  };

  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      try {
        const response = await SubscriptionService.getStatus();
        setSubscription(response.data);
        
        // Set the current plan as the selected plan if it's premium or ultimate
        if (response.data.planType !== 'basic') {
          setSelectedPlan(response.data.planType);
        }
      } catch (error) {
        console.error('Failed to fetch subscription status:', error);
        setError('Failed to load subscription information. Please try again.');
        
        // FOR DEVELOPMENT: Set default values to allow UI to render
        console.log('Using default subscription status for development');
        setSubscription({
          isPremium: false,
          planType: 'basic'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptionStatus();
  }, []);

  const handleUpgrade = async () => {
    try {
      setProcessingAction(true);
      setError(null);
      
      // Initialize payment for the selected plan
      const response = await SubscriptionService.upgrade(selectedPlan, billingPeriod);
      
      // Redirect to Paystack
      if (response.data && response.data.authorizationUrl) {
        window.location.href = response.data.authorizationUrl;
      } else {
        throw new Error('Failed to initialize payment');
      }
    } catch (error) {
      console.error('Upgrade failed:', error);
      setError('Failed to process payment. Please try again.');
      setProcessingAction(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will continue to have premium access until your current billing period ends.')) {
      return;
    }
    
    try {
      setProcessingAction(true);
      setError(null);
      
      await SubscriptionService.cancel();
      
      // Refresh status after cancellation
      const response = await SubscriptionService.getStatus();
      setSubscription(response.data);
      
      setSuccess('Your subscription has been cancelled. You will continue to have premium access until your subscription expires.');
      setTimeout(() => setSuccess(null), 5000);
    } catch (error) {
      console.error('Cancellation failed:', error);
      setError('Failed to cancel subscription. Please try again.');
    } finally {
      setProcessingAction(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
        <div className="h-32 bg-gray-200 rounded mb-4"></div>
        <div className="h-64 bg-gray-200 rounded"></div>
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
          {error}
          <button 
            className="absolute top-0 bottom-0 right-0 px-4 py-3"
            onClick={() => setError(null)}
          >
            <span className="sr-only">Dismiss</span>
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      )}
      
      {success && (
        <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded relative">
          {success}
        </div>
      )}
      
      {/* Current Plan Summary */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-6">
        <div className="p-4 sm:p-6">
          <div className="flex items-center">
            <div className={`flex-shrink-0 h-12 w-12 rounded-full flex items-center justify-center ${
              subscription.planType === 'premium' ? 'bg-pink-100 text-pink-600' :
              subscription.planType === 'ultimate' ? 'bg-green-100 text-green-600' :
              'bg-gray-100 text-gray-600'
            }`}>
              <Award className="h-6 w-6" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">
                {subscription.planType.charAt(0).toUpperCase() + subscription.planType.slice(1)} Plan
              </h3>
              <p className="text-sm text-gray-500">
                {subscription.isPremium
                  ? `Active until ${formatDate(subscription.expiresAt)}`
                  : 'Free plan with basic features'}
              </p>
              
              {subscription.isPremium && subscription.daysRemaining && (
                <div className="mt-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {subscription.daysRemaining} days remaining
                  </span>
                </div>
              )}
            </div>
          </div>
          
          {subscription.isPremium && (
            <div className="mt-6 flex flex-col sm:flex-row sm:space-x-4">
              <button
                onClick={handleCancel}
                disabled={processingAction}
                className="mt-3 sm:mt-0 inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
              >
                {processingAction ? 'Processing...' : 'Cancel Subscription'}
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Plan Selection */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-base font-medium text-gray-900">
            {subscription.isPremium ? 'Change Plan' : 'Upgrade to Premium'}
          </h3>
        </div>
        
        {/* Billing Period Toggle */}
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <div className="flex justify-center">
            <div className="bg-gray-100 p-1 rounded-full inline-flex">
              <button
                onClick={() => setBillingPeriod('monthly')}
                className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all ${
                  billingPeriod === 'monthly'
                    ? 'bg-white shadow-sm text-gray-900'
                    : 'text-gray-700 hover:text-gray-900'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingPeriod('yearly')}
                className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all ${
                    billingPeriod === 'yearly'
                    ? 'bg-white shadow-sm text-gray-900'
                    : 'text-gray-700 hover:text-gray-900'
                }`}
              >
                Yearly
                <span className="ml-1 bg-pink-600 text-white text-xs px-2 py-0.5 rounded-full">
                  Save 20%
                </span>
              </button>
            </div>
          </div>
        </div>
        
        {/* Plan Options */}
        <div className="px-4 py-5 sm:px-6">
          <div className="grid gap-4 sm:grid-cols-2">
            {/* Premium Plan */}
            <div 
              className={`border rounded-lg p-4 cursor-pointer transition-all ${
                selectedPlan === 'premium' ? 'border-pink-500 bg-pink-50' : 'border-gray-200 hover:border-pink-300'
              }`}
              onClick={() => setSelectedPlan('premium')}
            >
              <div className="flex justify-between items-start">
                <h4 className="text-base font-medium text-gray-900">Premium</h4>
                {selectedPlan === 'premium' && (
                  <Check className="h-5 w-5 text-pink-500" />
                )}
              </div>
              
              <div className="mt-1 text-sm text-gray-500">
                Perfect for couples who want more features
              </div>
              
              <div className="mt-4">
                <div className="text-lg font-semibold text-gray-900">
                  ${billingPeriod === 'monthly' ? planPricing.premium.monthly.usd : planPricing.premium.yearly.usd / 12}/month
                </div>
                <div className="text-sm text-gray-500">
                  {formatCurrency(billingPeriod === 'monthly' ? planPricing.premium.monthly.ngn : planPricing.premium.yearly.ngn / 12)}/month
                  {billingPeriod === 'yearly' && ' (billed yearly)'}
                </div>
              </div>
              
              <ul className="mt-4 space-y-2">
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                  <span className="text-sm text-gray-500">Custom Domain</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                  <span className="text-sm text-gray-500">Unlimited Images</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                  <span className="text-sm text-gray-500">Lower Withdrawal Fees</span>
                </li>
              </ul>
            </div>
            
            {/* Ultimate Plan */}
            <div 
              className={`border rounded-lg p-4 cursor-pointer transition-all ${
                selectedPlan === 'ultimate' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-green-300'
              }`}
              onClick={() => setSelectedPlan('ultimate')}
            >
              <div className="flex justify-between items-start">
                <h4 className="text-base font-medium text-gray-900">Ultimate</h4>
                {selectedPlan === 'ultimate' && (
                  <Check className="h-5 w-5 text-green-500" />
                )}
              </div>
              
              <div className="mt-1 text-sm text-gray-500">
                The complete wedding planning experience
              </div>
              
              <div className="mt-4">
                <div className="text-lg font-semibold text-gray-900">
                  ${billingPeriod === 'monthly' ? planPricing.ultimate.monthly.usd : planPricing.ultimate.yearly.usd / 12}/month
                </div>
                <div className="text-sm text-gray-500">
                  {formatCurrency(billingPeriod === 'monthly' ? planPricing.ultimate.monthly.ngn : planPricing.ultimate.yearly.ngn / 12)}/month
                  {billingPeriod === 'yearly' && ' (billed yearly)'}
                </div>
              </div>
              
              <ul className="mt-4 space-y-2">
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                  <span className="text-sm text-gray-500">Everything in Premium</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                  <span className="text-sm text-gray-500">Priority Support</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                  <span className="text-sm text-gray-500">Multi-currency Support (₦ & $)</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="mt-6">
            <button
              onClick={handleUpgrade}
              disabled={processingAction || (subscription.isPremium && subscription.planType === selectedPlan)}
              className={`w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
                selectedPlan === 'premium' 
                  ? 'bg-pink-600 hover:bg-pink-700' 
                  : 'bg-green-600 hover:bg-green-700'
              } focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                selectedPlan === 'premium' ? 'focus:ring-pink-500' : 'focus:ring-green-500'
              } ${
                (processingAction || (subscription.isPremium && subscription.planType === selectedPlan))
                  ? 'opacity-50 cursor-not-allowed'
                  : ''
              }`}
            >
              {processingAction
                ? 'Processing...'
                : subscription.isPremium
                  ? subscription.planType === selectedPlan
                    ? `You're already on ${selectedPlan} plan`
                    : `Switch to ${selectedPlan} plan`
                  : `Upgrade to ${selectedPlan} plan`
              }
            </button>
          </div>
          
          <div className="mt-4 text-sm text-center text-gray-500">
            {billingPeriod === 'monthly' 
              ? `You'll be charged ${formatCurrency(planPricing[selectedPlan].monthly.ngn)} monthly.`
              : `You'll be charged ${formatCurrency(planPricing[selectedPlan].yearly.ngn)} yearly.`
            } Includes a 14-day money-back guarantee.
          </div>
        </div>
      </div>
      </div>
    );
  };
  
  
  export default SubscriptionSettings;